import * as React from "react";
import axios from "axios";

import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import MuiAlert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";

import { Button } from "@mui/material";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import { Formik } from "formik";
import * as yup from "yup";

export default function AddOrg({
  setOpen,
  refresh,
  setRefresh,
  setAlertOpen,
  setAlertColour,
  setMessage,
}) {
  const [btnLoading, setBtnLoading] = React.useState(false);

  //list of orgs to check if name is unique
  const [allOrgs, setAllOrgs] = React.useState([]);

  //alert states:
  const [openPageAlert, setPageAlertOpen] = React.useState(false);
  const [pageMessage, setPageMessage] = React.useState("");
  const [pageAlertColour, setPageAlertColour] = React.useState();

  React.useEffect(() => {
    axios.get("/orgs").then((res) => {
      const orgs = res.data.orgs.map((org) => org.client);
      setAllOrgs(orgs);
    });
  }, []);

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setPageAlertOpen(false);
  };

  // Form validation:
  const userSchema = yup.object().shape({
    organization: yup
      .string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required")
      .matches(
        /^[a-zA-Z0-9\s\-]+$/,
        "Organisation name must only include letters and numbers"
      )
      .transform((value) => value.toLowerCase())
      .notOneOf(
        allOrgs.map((org) => org.toLowerCase()),
        "Organisation already exists"
      ),
    subscriptionType: yup.string().required("Required"),
    accountType: yup.string().required("Required"),
    endDate: yup.date().required("Required"),
    used: yup.number().min(1).required("Required"),
    tolerance: yup.number().min(0).max(100).required("Required"),
    remaining: yup.number().min(1).required("Required"),
    type: yup.string().required("Required"),
  });

  const date = new Date(Date.now());
  date.setFullYear(date.getFullYear() + 1);

  const initialValues = {
    organization: "",
    subscriptionType: "",
    accountType: "",
    endDate: date,
    used: 0,
    tolerance: 0,
    remaining: 0,
    type: "",
  };

  function dateFormat(date) {
    const newDate =
      "" +
      dayjs(date).year() +
      "-" +
      ("0" + (dayjs(date).month() + 1)).substring(
        ("0" + (dayjs(date).month() + 1)).length - 2
      ) +
      "-" +
      ("0" + dayjs(date).date()).substring(
        ("0" + dayjs(date).date()).length - 2
      );

    return newDate;
  }

  const handleSubmit = (values, { resetForm }) => {
    values.endDate = dateFormat(values.endDate);
    setBtnLoading(true);
    resetForm({ values: "" });
    axios
      .post(`/addorg`, { values: values })
      .then((res) => {
        setPageAlertColour("success");
        setPageMessage(res.data);
        setPageAlertOpen(true);
        setBtnLoading(false);

        if (setOpen) {
          setOpen(false);
          setRefresh(refresh + 1);
          setAlertColour("success");
          setMessage(res.data);
          setAlertOpen(true);
        }
        resetForm({ values: "" });
      })
      .catch((err) => {
        setPageAlertColour("error");
        setPageMessage(err.response.data);
        setPageAlertOpen(true);
        setBtnLoading(false);
      });
  };

  return (
    <Box p="20px" sx={{ backgroundColor: "#212121", mt: "5px", mb: "5px" }}>
      <Snackbar
        open={openPageAlert}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        {
          <Alert
            onClose={handleAlertClose}
            severity={pageAlertColour}
            sx={{ width: "100%" }}
          >
            {pageMessage}
          </Alert>
        }
      </Snackbar>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={userSchema}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {/* <Title>Create New Organization</Title> */}
        {({
          values,
          errors,
          touched,
          resetForm,
          handleSubmit,
          handleChange,
          setFieldValue,
          handleBlur,
          setFieldTouched,
          isValidating,
        }) => (
          <form onSubmit={handleSubmit} onReset={resetForm}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <TextField
                margin="dense"
                fullWidth
                name="organization"
                label="Organization name"
                variant="standard"
                value={values.organization}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.organization && !!errors.organization}
                helperText={touched.organization && errors.organization}
              />

              <TextField
                select
                fullWidth
                margin="dense"
                variant="standard"
                name="subscriptionType"
                value={values.subscriptionType}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Subscription type"
                error={!!touched.subscriptionType && !!errors.subscriptionType}
                helperText={touched.subscriptionType && errors.subscriptionType}
              >
                <MenuItem key={"premium"} value="premium">
                  premium
                </MenuItem>
                <MenuItem key={"standard"} value="standard">
                  standard
                </MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                margin="dense"
                variant="standard"
                name="accountType"
                value={values.accountType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.accountType && !!errors.accountType}
                helperText={touched.accountType && errors.accountType}
                label="Default account type"
              >
                <MenuItem key={"plus"} value="plus">
                  plus
                </MenuItem>
                <MenuItem key={"standard"} value="standard">
                  standard
                </MenuItem>
                <MenuItem key={"trial"} value="trial">
                  trial
                </MenuItem>
                <MenuItem key={"free"} value="free">
                  free
                </MenuItem>
              </TextField>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="Expiry date"
                  inputFormat="DD/MM/YYYY"
                  disablePast
                  variant="standard"
                  name="endDate"
                  value={values.endDate}
                  error={!!touched.endDate && !!errors.endDate}
                  helperText={touched.endDate && errors.endDate}
                  onChange={(newValue) => {
                    setFieldValue("endDate", newValue);
                    setFieldTouched("endDate", true);
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        fullWidth
                        margin="dense"
                        variant="standard"
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    );
                  }}
                />
              </LocalizationProvider>

              <TextField
                InputProps={{ inputProps: { min: 0 } }}
                margin="dense"
                fullWidth
                name="used"
                value={values.used}
                variant="standard"
                onChange={handleChange}
                onBlur={handleBlur}
                type="number"
                label="Maximum number of users"
                error={!!touched.used && !!errors.used}
                helperText={touched.used && errors.used}
              ></TextField>
              <TextField
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="dense"
                fullWidth
                name="tolerance"
                value={values.tolerance}
                variant="standard"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.tolerance && !!errors.tolerance}
                helperText={touched.tolerance && errors.tolerance}
                label="Risk tolerance"
              ></TextField>
              <TextField
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="dense"
                fullWidth
                name="remaining"
                value={values.remaining}
                variant="standard"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.remaining && !!errors.remaining}
                helperText={touched.remaining && errors.remaining}
                label="Maximum number of RiskTree calculations"
              ></TextField>
              <TextField
                select
                margin="dense"
                fullWidth
                name="type"
                value={values.type}
                variant="standard"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.type && !!errors.type}
                helperText={touched.type && errors.type}
                label="Key type"
              >
                <MenuItem key={"org"} value="org">
                  org
                </MenuItem>
                <MenuItem key={"cons"} value="cons">
                  cons
                </MenuItem>
              </TextField>

              <LoadingButton
                loading={btnLoading || isValidating}
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Add
              </LoadingButton>
              <Button type="reset" variant="contained" color="secondary">
                Reset
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
}
